<template>
  <div class="form-container has-text-left">
    <h1 class="is-size-5 has-text-weight-semibold">Make Payment</h1>
    <p>Make sure all provided information is correct.</p>
    <br>
    <div class="level is-mobile mb-0">
      <div class="level-left">

        <div class="level-item">

          <p class="has-text-weight-bold">Signup info</p>
          &nbsp;
          <router-link
            :to="{ name: 'register-profile'}"
            class="button is-small is-info"
          >
            Edit
          </router-link>
        </div>
      </div>
    </div>

    <p
      v-if="guardianConsent"
      class="has-text-weight-bold mt-15"
    >Participant</p>
    <table class="table is-fullwidth">
      <tbody>
        <tr>
          <td class="w-50">Name</td>
          <th class="w-50">{{ firstName }} {{ lastName }}</th>
        </tr>
        <tr>
          <td class="w-50">Email</td>
          <th class="w-50">{{ email }}</th>
        </tr>
        <tr>
          <td class="w-50">Date of Birth</td>
          <th class="w-50">{{ formattedDate() }}</th>
        </tr>
      </tbody>
    </table>

    <p
      v-if="guardianConsent"
      class="has-text-weight-bold mt-15"
    >Parent/Guardian</p>
    <table
      v-if="guardianConsent"
      class="table is-fullwidth"
    >
      <tbody>
        <tr>
          <td class="w-50">Name</td>
          <th class="w-50">{{ guardianFirstName }} {{ guardianLastName }}</th>
        </tr>
        <tr>
          <td class="w-50">Email</td>
          <th class="w-50">{{ guardianEmail }}</th>
        </tr>
      </tbody>
    </table>

    <p class="has-text-weight-bold mt-15">Address</p>

    <table class="table is-fullwidth">
      <tbody>
        <tr>
          <td>{{ streetName }}<br>{{ city }} {{ state.code }} {{ zipCode }} — {{ country.name }}</td>
        </tr>
      </tbody>
    </table>

    <p class="has-text-weight-semibold">Competing divisions</p>
    <table class="table is-fullwidth">
      <tbody>
        <tr
          v-for="(division, index) in divisions"
          :key="index"
        >
          <td class="w-50">{{ division.name }}</td>
          <td class="w-50">${{ formatPrice(division.price) }}</td>
        </tr>
        <tr>
          <td>Total</td>
          <th>${{ getTotal() }}</th>
        </tr>
      </tbody>
    </table>

    <!--p class="has-text-weight-semibold">Card details</p-->

    <div class="field">
      <label
        class="label"
        v-text="'Card details'"
      />
      <div class="control">
        <div class="card-wrapper">
          <div id="card" />
        </div>
      </div>
      <p
        v-if="cardError"
        class="help is-danger"
        v-text="cardError"
      />
    </div>

    <div class="columns">
      <div class="column">
        <a
          href="#"
          class="button is-warning is-fullwidth has-text-weight-bold"
          :class="{ 'is-loading': isPaying }"
          :disabled="!canPay || isPaying"
          @click.prevent="pay"
        >Pay</a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import stripeStyles from '@/configs/stripe'
import priceMixin from '@/mixins/price'

export default {
  name: 'PaymentConfirmation',
  mixins: [priceMixin],
  data () {
    return {
      stripe: '',
      cardEl: '',
      //  State
      canPay: false,
      isPaying: false,
      cardError: false
    }
  },
  mounted () {
    setTimeout(() => {
      // eslint-disable-next-line
      this.stripe = Stripe(process.env.VUE_APP_STRIPE_PK)

      const elements = this.stripe.elements()

      this.cardEl = elements.create('card', stripeStyles)
      this.cardEl.mount('#card')

      this.cardEl.on('change', (payload) => {
        this.canPay = payload.complete
        if (typeof payload.error !== 'undefined') {
          this.cardError = payload.error.message
        } else {
          this.cardError = false
        }
      })
    }, 1000)
  },
  computed: {
    ...mapState('register', {
      firstName: state => state.firstName,
      lastName: state => state.lastName,
      email: state => state.email,
      birthdate: state => state.birthdate,
      stripeClientSecret: state => state.stripeClientSecret,
      divisions: state => state.divisions,
      guardianConsent: state => state.guardianConsent,
      guardianFirstName: state => state.guardianFirstName,
      guardianLastName: state => state.guardianLastName,
      guardianEmail: state => state.guardianEmail,
      country: state => state.country,
      state: state => state.state,
      streetName: state => state.streetName,
      city: state => state.city,
      zipCode: state => state.zipCode
    })
  },
  methods: {
    formattedDate () {
      return `${this.birthdate.getDate()} / ${this.birthdate.getMonth() + 1} / ${this.birthdate.getFullYear()}`
    },
    price (value) {
      return Math.round(value / 1000 * 10).toFixed(2)
    },
    getTotal () {
      let tt = 0
      this.divisions.map(item => {
        tt += parseInt(item.price)
      })
      return Math.round(tt / 1000 * 10).toFixed(2)
    },
    async pay () {
      if (!this.isPaying) {
        this.isPaying = true
        const { error } = await this.stripe.handleCardPayment(
          this.stripeClientSecret, this.cardEl, {
            payment_method_data: {
              billing_details: { name: `${this.firstName} ${this.lastName}` }
            }
          })

        if (!error) {
          this.$router.push({ name: 'register-complete' })
        } else {
          this.isPaying = false
          console.log(error)
          this.$toasted.error(error.message)
        }
      }
    }
  }
}
</script>

<style lang="scss">

  .level.mb-0{
    margin-bottom: 0;
  }
  .w-50{
    width: 50%;
  }

  .card-wrapper{
    background: #FFF;
    margin: .5em 0;
  }

  // Replicate Bulma's default input styles & states
  .StripeElement{
    width: 100%;
    height: 2.25em;
    border: 1px solid #dbdbdb;
    border-radius: 4px;
    padding-bottom: calc(.375em - 1px);
    padding-left: calc(.625em - 1px);
    padding-right: calc(.625em - 1px);
    padding-top: calc(.375em + 1px);
    box-shadow: inset 0 1px 2px rgba(10, 10, 10, 0.1);
    -webkit-box-shadow: inset 0 1px 2px rgba(10, 10, 10, 0.1);
    &:hover{
      border-color: #b5b5b5;
    }
    &--focus, &--focus:hover{
      border-color: #3273dc;
      box-shadow: 0 0 0 0.125em rgba(50,115,220,.25);
    }
  }
</style>
